import { Fragment, useMemo } from 'react';
import { FireOutlined } from '@ant-design/icons';
import type { DashboardHeatmapCell } from '@/types/dashboard';
import { formatInr } from '@/utils/currency';
import { buildHeatmapGrid } from '@/utils/dashboardHeatmap';
import {
  HEATMAP_BLOCK_LABELS,
  HEATMAP_BLOCKS,
  HEATMAP_DAY_LABELS,
} from '../constants/heatmap.constants';
import { InsightCardHeader } from './InsightCardHeader';
import styles from '../Dashboard.module.scss';

interface OrderHeatmapProps {
  data: DashboardHeatmapCell[];
  totalOrders?: number;
}

export function OrderHeatmap({ data = [], totalOrders }: OrderHeatmapProps) {
  const { grid, maxCount, peakLabel, displayTotal } = useMemo(
    () => buildHeatmapGrid(data, totalOrders),
    [data, totalOrders],
  );

  return (
    <div className={styles.heatmapCard}>
      <InsightCardHeader
        icon={<FireOutlined />}
        title="Order activity heatmap"
        subtitle="When customers place orders — day × 3-hour blocks (IST)"
        badge={`${displayTotal} orders`}
      />

      <div className={styles.heatmapBody}>
        {displayTotal > 0 ? (
          <>
            <div className={styles.heatmapGrid}>
              <div className={styles.heatmapCorner} />
              {HEATMAP_BLOCK_LABELS.map((label) => (
                <div key={label} className={styles.heatmapHourLabel}>
                  {label}
                </div>
              ))}

              {HEATMAP_DAY_LABELS.map((dayLabel, dayIndex) => (
                <Fragment key={dayLabel}>
                  <div className={styles.heatmapDayLabel}>{dayLabel}</div>
                  {Array.from({ length: HEATMAP_BLOCKS }, (_, block) => {
                    const cell = grid.get(`${dayIndex}-${block}`);
                    const count = cell?.count ?? 0;
                    const intensity = maxCount > 0 ? count / maxCount : 0;
                    const title = cell
                      ? `${dayLabel} ${HEATMAP_BLOCK_LABELS[block]} — ${count} orders, ${formatInr(cell.revenue)}`
                      : `${dayLabel} ${HEATMAP_BLOCK_LABELS[block]} — no orders`;

                    return (
                      <div
                        key={`${dayIndex}-${block}`}
                        className={`${styles.heatmapCell} ${count > 0 ? styles.heatmapCellActive : ''}`}
                        style={{
                          backgroundColor: count
                            ? `rgba(155, 126, 84, ${0.2 + intensity * 0.8})`
                            : undefined,
                        }}
                        title={title}
                      >
                        {count > 0 ? (
                          <span className={styles.heatmapCellValue}>{count}</span>
                        ) : null}
                      </div>
                    );
                  })}
                </Fragment>
              ))}
            </div>

            <div className={styles.heatmapFooter}>
              <div className={styles.heatmapLegend}>
                <span>Low</span>
                <div className={styles.heatmapLegendBar} />
                <span>High</span>
              </div>
              {peakLabel ? (
                <span className={styles.heatmapPeak}>
                  Peak: <strong>{peakLabel}</strong>
                </span>
              ) : null}
            </div>
          </>
        ) : (
          <div className={styles.heatmapEmpty}>
            No order timing data yet. Activity will appear as customers place orders.
          </div>
        )}
      </div>
    </div>
  );
}
